import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@jornada/shared";
import { readAccountsStateSync } from "@/lib/accounts";
import { resolveActiveAccountView } from "@/lib/active-account";
import { readTabAccountId } from "@/lib/tab-account";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * Jornada auth wrapper.
 *
 * NOTE (#13 — de-clone): Jornada deliberately does NOT use the shared
 * `AuthLayout` (the split brand-panel + glass card shell every clone inherited).
 * Instead each screen owns its own `AuthScene` — a utilitarian "field sheet":
 * a console header bar over a left-rail form, flat on the ambient background. So
 * this route is a thin wrapper: it only owns the "already authenticated →
 * redirect" gate and the page transition. The background, brand console and
 * scroll container live in `AuthScene`, rendered per-screen.
 */
const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

/**
 * Synchronizes machine and cache with what the server responded.
 *
 * This is ALWAYS done when the response arrives, even if the timeout already
 * expired and login was rendered: the request is not cancelled. This prevents
 * the `BootManager` from seeing a "pending" or stale session during the route
 * transition, which is what caused redirect loops.
 */
const syncReply = (reply: FetchResult): FetchResult => {
  const { data: replyData, error: replyError } = reply;
  sessionActor.send({
    type: "SERVER_REPLY",
    session: isValidSession(replyData) ? cloneSession(replyData) : null,
    error: replyError ?? null,
  });
  queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
  return reply;
};

/** Session check that never rejects: a network failure resolves to `null`. */
const startSessionCheck = async (): Promise<FetchResult | null> => {
  try {
    return syncReply(await fetchSession());
  } catch {
    // Network error — let user try to log in anyway.
    return null;
  }
};

/** Expires after `SESSION_CHECK_TIMEOUT_MS` and resolves to `null`. */
const sessionCheckDeadline = async (): Promise<null> => {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, SESSION_CHECK_TIMEOUT_MS);
  });
  return null;
};

function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

/**
 * Auth route guard: redirects an already-authenticated visitor away from the
 * login screen. Exported so its test can call it directly.
 */
export async function authBeforeLoad({ search }: { search: { mode?: string; redirect?: string } }) {
  // Multi-account: `?mode=add` opens /login to ADD another account while the
  // current one stays signed in (Gmail style). The "already authenticated →
  // go home" gate must be skipped here; otherwise the switcher's "Add account"
  // would bounce straight back to the app and a 2nd login could never be added.
  const isAddingAccount = search.mode === "add";
  if (isAddingAccount) {
    return;
  }

  // LAYERING (boot safety): only take this branch when the tab has an EXPLICIT
  // pin — the faithful analogue of the old "the redirect param carried a
  // `/u/<n>`" condition. A tab with no pin yet (cold boot) falls through to the
  // baseline global-session fast path below, preserving every 401 / sign-out /
  // reload transition the BootManager depends on.
  const redirectTarget = search.redirect;
  const tabAccountId = readTabAccountId();
  if (tabAccountId !== null) {
    const view = resolveActiveAccountView(
      readAccountsStateSync(),
      tabAccountId,
      sessionActor.getSnapshot().context.session,
    );
    // Guest tab → show /login (do NOT redirect). Real account → bounce home.
    if (view.isAuthenticated) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: redirectTarget ?? "/" });
    }
    return;
  }

  // Fast path: check machine state before hitting the network.
  const snapshot = sessionActor.getSnapshot();
  if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
    if (snapshot.context.session) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: search.redirect ?? "/" });
    }
    return;
  }

  // Network check — only if machine has no session yet.
  // Use api.GET (not fetchApi) so we can inspect the status without throwing.
  //
  // P23/CC-60 · I1-bis: this `await` must NOT be unbounded. A STALLED
  // network request (held, neither 401 nor 5xx: both resolve quickly) left this
  // `beforeLoad` unresolved, the router not mounting the route and `/login`
  // RENDERING NOTHING — `#root` without children at 3/6/10/15/20 s, measured also
  // on the production bundle. The sibling guard of `_app` already ran with
  // a ceiling; this one was left without it, which is precisely why I1 closed green
  // (phase 1 measured `/`, which was healthy, and not `/login`).
  //
  // The request is NOT cancelled: it stays alive and `syncReply` synchronizes machine +
  // cache when it arrives. If there turned out to be a session, the `BootManager`
  // (SESSION_RESOLVED → `checkingNavigation` → `redirecting`) transitions the user
  // away from the login screen: only a flicker is paid, never a blank
  // screen.
  const result = await Promise.race([startSessionCheck(), sessionCheckDeadline()]);
  if (!result) {
    // Deadline expired or network error: the login is RENDERED. The late response
    // stays hooked up above and the BootManager redirects if it brings a session.
    return;
  }

  const { data } = result;

  // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
  // Better Auth returns HTTP 200 with a null body for unauthenticated requests,
  // but the OpenAPI schema types Session as non-null. Cast to match runtime reality.
  // Redirect on the session the MACHINE accepted, not on the raw reply: a reply for a
  // session that LOGOUT revoked is refused by the machine and must not bounce the guest.
  const sessionData = data as { user?: unknown } | null | undefined;
  if (sessionData?.user && sessionActor.getSnapshot().context.session) {
    // oxlint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: search.redirect ?? "/" });
  }
  // 401/403, network error, or null session — not authenticated, show login page.
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: authBeforeLoad,
  component: AuthLayoutRoute,
});
